<template>
  <tm-card :class="{ 'mobile-view': $isMobile() }">
    <template #header>
      <tm-breadcrumb :current="$t('scenarios.scenarios')" />
      <tm-heading :text="$t('scenarios.scenarios') + dtaModelFlag" back-to-route="home" />
      <tm-divider />
      <tm-filter
        v-model="filteredScenarios"
        :ready="contentLoaded"
        :items="scenarios"
        cache-key="scenarios"
        :filtering-options="filteringOptions"
        :sorting-options="sortingOptions"
      />
    </template>

    <template #content>
      <tm-panel-list
        :ready="contentLoaded"
        :items="filteredScenarios"
        :activated-item-id="activeScenarioId"
        :watch-mode="!isLoggedUser || !isTrafficModelValid"
        type="scenarios"
        @item:activated="$store.dispatch('map/activateItem', { scenarioId: $event.id })"
        @item:deactivated="$store.dispatch('map/activateItem', {})"
        @item:action:clicked="performItemAction($event.action, $event.item)"
        @icon:clicked="(scenario) => handleIconClick(scenario)"
      />
    </template>

    <template #footer>
      <div v-if="isLoggedUser && isTrafficModelValid" class="p-d-flex p-jc-center">
        <tm-button
          :tooltip="$t('scenarios.new scenario')"
          type="create"
          icon="pi-plus"
          class="p-mr-3"
          @click="beginScenarioCreation()"
        />
      </div>
    </template>
  </tm-card>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import useEditMode from '@composables/useEditMode';
import useFullScenario from '@composables/useFullScenario';
import TmPanelList from '@components/views/PanelList.vue';
import TmFilter from '@components/views/Filter.vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import useModels from '@composables/useModels';
import type { Filters, Sorting } from '@composables/useFilters';
import type { ItemAction } from '@components/views/PanelListItem.vue';

const { fetchModels } = useModels();
const { enterEditSession, verifyEditSessions } = useEditMode();
const {
  scenariosOverview: scenarios,
  fetchScenariosOverview: fetchScenarios,
  deleteScenario,
  copyScenario,
  activeScenarioId,
} = useFullScenario();
const { t } = useI18n();
const store = useStore();
const router = useRouter();
const confirm = useConfirm();

const isLoggedUser = computed<boolean>(() => store.getters['auth/isLogged']);
const isTrafficModelValid = computed<boolean>(() => !!store.state.scenarios.model?.isValid);
const loggedUserId: number = store.state.auth.user.id;
const contentLoaded = ref(false);
const filteredScenarios = ref<TmScenario[]>([]);
const dtaModelFlag = computed(() => (store.getters['scenarios/isDtaModelTypeActive'] ? ' (DTA)' : ''));

const filteringOptions: Filters = {
  sharing: [
    { name: 'own', field: 'authorUserId', value: loggedUserId, label: 'scenarios.private', default: !!loggedUserId },
    { name: 'shared', field: 'isShared', value: true, label: 'scenarios.shared' },
    { name: 'public', field: 'isPublic', value: true, label: 'scenarios.public' },
  ],
  date: [
    { name: 'past', label: 'scenarios.past' },
    { name: 'current', label: 'scenarios.current' },
    { name: 'future', label: 'scenarios.future' },
  ],
};

const sortingOptions: Sorting[] = [
  { name: 'name', label: 'scenarios.sort by name', default: true },
  { name: 'date', label: 'scenarios.sort by most recent' },
  { name: 'dateFrom', label: 'scenarios.sort by date from' },
  { name: 'dateTo', label: 'scenarios.sort by date to' },
];

const performItemAction = async (action: ItemAction, scenario: TmScenario) => {
  switch (action) {
    case 'enter':
      await enterEditSession(scenario);
      await store.dispatch('map/activateItem', { scenarioId: scenario.id }); // this is only needed to switch data mode to model if it is not already
      router.push({ name: 'scenarios.events', params: { id: scenario.id } });
      break;
    case 'share':
      router.push({ name: 'scenarios.share', params: { id: scenario.id } });
      break;
    case 'copy':
      copyScenario(scenario.id);
      break;
    case 'delete':
      confirm.require({
        message: t('scenarios.confirm delete', { name: scenario.name }),
        header: t('scenarios.delete scenario'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          deleteScenario(scenario.id);
        },
      });
  }
};

const beginScenarioCreation = async () => {
  await store.dispatch('map/activateItem', {}); // cancel any active scenario first
  router.push({ name: 'scenarios.create' });
};

const handleIconClick = (scenario: TmScenario) => {
  if (!scenario) return;
  enterEditSession(scenario, { forceLoadChanges: true });
  router.push({ name: 'scenarios.events', params: { id: scenario?.id } });
};

onMounted(async () => {
  await fetchModels(); // models should be prefetched, so we have the active traffic model ID available to fetch corresponding scenarios
  await fetchScenarios();
  contentLoaded.value = true;
  verifyEditSessions();
});
</script>

<style scoped>
.mobile-view .p-divider,
.mobile-view :deep(.mobile-breadcrumb),
.mobile-view :deep(.tm-back),
.mobile-view :deep(.footer) {
  display: none;
}
</style>
